import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useUserStore from '../../store/useUserStore';
import { 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import { api } from '../../config/api.config';

const SalesPolicy = () => {
  const nav = useNavigate();
  const { userToken, isTokenValid } = useUserStore();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // 설정 키별 메타 정보
  const settingsMeta = {
    'refund_per_wash_price': {
      label: '1회 차감액',
      unit: '원',
      description: '환불 시 세차 1회당 차감되는 금액',
      type: 'number',
      min: 0,
      step: 1000
    },
    'refund_penalty_rate_early': {
      label: '0~7일 위약금 비율',
      unit: '%',
      description: '결제일로부터 0~7일 이내 환불 시 부과되는 위약금 비율',
      type: 'percent',
      min: 0,
      max: 100,
      step: 1
    },
    'refund_penalty_rate_late': {
      label: '8~14일 위약금 비율',
      unit: '%',
      description: '결제일로부터 8~14일 이내 환불 시 부과되는 위약금 비율',
      type: 'percent',
      min: 0,
      max: 100,
      step: 1
    },
    'refund_cutoff_days': {
      label: '환불 불가 기준일',
      unit: '일',
      description: '이 일수 이후는 환불 불가, 해지만 가능',
      type: 'number',
      min: 1,
      max: 90,
      step: 1
    }
  };

  useEffect(() => {
    if (!userToken || !isTokenValid()) {
      alert('관리자 권한이 없습니다.');
      nav('/login');
      return;
    }
    fetchSettings();
  }, [userToken, isTokenValid, nav]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings');
      
      if (response.data.success) {
        // 환불 관련 설정만 필터링
        const refundSettings = response.data.settings.filter(s => 
          s.key.startsWith('refund_')
        );
        setSettings(refundSettings);
      }
    } catch (error) {
      console.error('설정 조회 오류:', error);
      setSnackbar({
        open: true,
        message: '설정을 불러오는 중 오류가 발생했습니다.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setting) => {
    setEditingKey(setting.key);
    
    // 퍼센트 타입은 0~1을 0~100으로 변환해서 표시
    const meta = settingsMeta[setting.key];
    if (meta?.type === 'percent') {
      setEditValue((setting.value * 100).toString());
    } else {
      setEditValue(setting.value.toString());
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleSave = async (key) => {
    try {
      const meta = settingsMeta[key];
      let valueToSave = editValue;

      // 퍼센트 타입은 0~100을 0~1로 변환
      if (meta?.type === 'percent') {
        valueToSave = parseFloat(editValue) / 100;
      } else if (meta?.type === 'number') {
        valueToSave = parseInt(editValue);
      }

      // 유효성 검증
      if (meta?.min !== undefined && valueToSave < meta.min) {
        setSnackbar({
          open: true,
          message: `최소값은 ${meta.min}입니다.`,
          severity: 'warning'
        });
        return;
      }
      if (meta?.max !== undefined && valueToSave > meta.max) {
        setSnackbar({
          open: true,
          message: `최대값은 ${meta.max}입니다.`,
          severity: 'warning'
        });
        return;
      }

      const response = await api.put(`/settings/${key}`, {
        value: valueToSave,
        description: meta?.description
      });

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: '설정이 저장되었습니다.',
          severity: 'success'
        });
        setEditingKey(null);
        setEditValue('');
        fetchSettings(); // 설정 다시 불러오기
      }
    } catch (error) {
      console.error('설정 저장 오류:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || '설정 저장 중 오류가 발생했습니다.',
        severity: 'error'
      });
    }
  };

  const formatValue = (setting) => {
    const meta = settingsMeta[setting.key];
    
    if (meta?.type === 'percent') {
      return `${(setting.value * 100).toFixed(0)}%`;
    } else if (meta?.type === 'number') {
      return `${setting.value.toLocaleString('ko-KR')}${meta.unit}`;
    }
    return setting.value;
  };

  if (!userToken || !isTokenValid()) {
    return null;
  }

  return (
    <div>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h4" gutterBottom>
          판매정책 관리
        </Typography>
        <Chip 
          icon={<InfoIcon />}
          label="환불/정기결제 정책 설정" 
          color="primary" 
          variant="outlined"
        />
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          ⚠️ 설정 변경 시 <strong>다음 환불 요청부터 즉시 반영</strong>됩니다. 
          기존 구독에는 영향을 주지 않으며, 프론트엔드 앱의 안내 문구도 자동으로 업데이트됩니다.
        </Typography>
      </Alert>

      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
              <TableCell sx={{ fontWeight: 'bold', color: '#1976d2', width: '20%' }}>
                설정 항목
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#1976d2', width: '35%' }}>
                설명
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#1976d2', width: '20%' }}>
                현재 값
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#1976d2', width: '25%' }}>
                작업
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                  설정을 불러오는 중...
                </TableCell>
              </TableRow>
            ) : settings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                  설정 데이터가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              settings.map((setting) => {
                const meta = settingsMeta[setting.key];
                const isEditing = editingKey === setting.key;

                return (
                  <TableRow key={setting.key} hover>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {meta?.label || setting.key}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {meta?.description || setting.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          size="small"
                          fullWidth
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {meta?.type === 'percent' ? '%' : meta?.unit}
                              </InputAdornment>
                            )
                          }}
                          inputProps={{
                            min: meta?.min,
                            max: meta?.type === 'percent' ? 100 : meta?.max,
                            step: meta?.step
                          }}
                        />
                      ) : (
                        <Chip 
                          label={formatValue(setting)} 
                          color="primary" 
                          sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<SaveIcon />}
                            onClick={() => handleSave(setting.key)}
                          >
                            저장
                          </Button>
                          <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            startIcon={<CancelIcon />}
                            onClick={handleCancel}
                          >
                            취소
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleEdit(setting)}
                        >
                          수정
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 설정 변경 이력 (향후 구현) */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#666' }}>
          💡 참고사항
        </Typography>
        <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
          <Typography variant="body2" paragraph>
            <strong>• 1회 차감액:</strong> 환불 시 세차 이용 횟수당 차감되는 금액입니다. 
            예를 들어 16,000원으로 설정하고 사용자가 세차를 2회 이용했다면, 환불 시 32,000원이 차감됩니다.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>• 위약금 비율:</strong> 결제일로부터 경과한 일수에 따라 부과되는 위약금 비율입니다. 
            0~7일은 조기 환불, 8~14일은 후기 환불로 구분됩니다.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>• 환불 불가 기준일:</strong> 기본 15일로 설정되어 있으며, 이 일수 이후에는 환불이 불가능하고 해지만 가능합니다.
          </Typography>
          <Typography variant="body2" color="error">
            <strong>⚠️ 주의:</strong> 설정 변경은 즉시 반영되므로 신중하게 수정해주세요. 
            기존 구독에는 영향을 주지 않으며, 향후 환불 요청부터 적용됩니다.
          </Typography>
        </Paper>
      </Box>

      {/* 스낵바 알림 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SalesPolicy;
